/**
 * The dataset step's picker. First where the cases come from, as three cards:
 * an existing dataset, a CSV to upload, or traces to pull. Picking an existing
 * one is a list beside the picked dataset's preview (`detail`), so a long list
 * never pushes the preview off screen; datasets with no items are tucked away.
 * The two "new" sources each say what happens before anything is made.
 */

import { Database, Upload, Waypoints } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import type { Dataset } from "@/features/datasets/types";
import { formatCount } from "@/lib/format";
import { ChoiceCard } from "./choice-card";
import { DatasetList } from "./dataset-list";

type Source = "existing" | "csv" | "traces";

export function DatasetPicker(props: {
  datasets: Dataset[] | undefined;
  datasetId: string | null;
  onChange: (id: string) => void;
  /** "Upload CSV": a new dataset whose items come from a file. */
  onUpload: () => void;
  /** "Pull from traces": a new live dataset on a trace source. */
  onPull: () => void;
  /** What the picked dataset holds, shown beside the list. */
  detail: ReactNode;
}) {
  const none = props.datasets?.length === 0;
  const [source, setSource] = useState<Source>(none ? "csv" : "existing");
  // A dataset picked from outside (one just pulled, or an upload's return) shows in the list.
  const [shownId, setShownId] = useState(props.datasetId);
  if (props.datasetId !== shownId) {
    setShownId(props.datasetId);
    setSource("existing");
  }
  return (
    <div className="flex flex-col gap-4">
      <fieldset className="grid gap-2.5 sm:grid-cols-3">
        <legend className="sr-only">Where the cases come from</legend>
        <ChoiceCard
          name="ne-source"
          icon={<Database className="size-4" />}
          checked={source === "existing"}
          disabled={none}
          onPick={() => setSource("existing")}
          note={none ? "No datasets yet." : `${formatCount(props.datasets?.length ?? 0)} in the workbench`}
        >
          Use an existing dataset
        </ChoiceCard>
        <ChoiceCard
          name="ne-source"
          icon={<Upload className="size-4" />}
          checked={source === "csv"}
          onPick={() => setSource("csv")}
          note="One row per case, from a file"
        >
          Upload a CSV
        </ChoiceCard>
        <ChoiceCard
          name="ne-source"
          icon={<Waypoints className="size-4" />}
          checked={source === "traces"}
          onPick={() => setSource("traces")}
          note="Cases from production traces"
        >
          Pull from traces
        </ChoiceCard>
      </fieldset>
      {source === "existing" && (
        <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
          <DatasetList
            datasets={props.datasets ?? []}
            datasetId={props.datasetId}
            onChange={props.onChange}
          />
          <div className="min-w-0">{props.detail}</div>
        </div>
      )}
      {source === "csv" && (
        <NewSource
          steps={[
            "Name the dataset.",
            "Upload the file and say which column is the input, the expected output and so on.",
            "Come back here with it picked.",
          ]}
          action={<Button onClick={props.onUpload}>Name it and upload</Button>}
        />
      )}
      {source === "traces" && (
        <NewSource
          steps={[
            "Pick a trace source and the traces to keep (filters).",
            "Each run pulls the matching traces of a period into a new version.",
            "Their outputs are scored as they ran in production.",
          ]}
          action={<Button onClick={props.onPull}>Set up the pull</Button>}
        />
      )}
    </div>
  );
}

function NewSource({ steps, action }: { steps: string[]; action: ReactNode }) {
  return (
    <div className="flex flex-col gap-4 rounded-[9px] border border-dashed border-drop-border bg-drop-bg p-5 sm:flex-row sm:items-end">
      <ol className="flex flex-1 flex-col gap-2">
        {steps.map((s, i) => (
          <li key={s} className="flex items-baseline gap-2.5 text-[13px] text-body">
            <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-surface text-[11px] font-semibold text-link">
              {i + 1}
            </span>
            {s}
          </li>
        ))}
      </ol>
      {action}
    </div>
  );
}
